import { useEffect, useMemo } from "react";
import { Menu, MenuProps } from "antd";
import { useLocation } from "react-router-dom";
import { useMercuryContext } from "../../context";
import { Link } from "react-router-dom";
import { DownOutlined } from "@ant-design/icons";
import { checkPermissions } from "../../common/utils/checkPermissions";
import { getFirstAccessibleGoogleRoute } from "../../apps/google/googleAccessRoutes";
import { normalizeDashboardRoute } from "../../common/utils/dashboardRoutes";
import { adjPermission } from "../../areas/adjustments-var/permissions";
import { ROUTES } from "../../common/constants/routes";

interface LeftMenuProps {
  adminMenuData: {
    handleMainMenuSelection: (key: string | null) => void;
    currentMenuItem: string;
  };
  mobile?: boolean;
}

const LeftMenu: React.FC<LeftMenuProps> = ({
  adminMenuData,
  mobile = false,
}) => {
  const { isAdmin, userDetails, flags, selectedApp } = useMercuryContext();
  const userPermissions = userDetails?.permissions || [];

  const location = useLocation();

  const canViewManualProjects = checkPermissions(userPermissions, [
    {
      customerApplication: "Google",
      permissionName: "Manual Projects",
      action: "read",
    },
  ]);

  // Legacy adjustments has no permission of its own; the new VAR app requires
  // Invoicing.read (see src/areas/adjustments-var/AdjustmentsVar.tsx).
  const canViewAdjustments = !flags?.newAdjustmentsApp || checkPermissions(userPermissions, [adjPermission("read")]);

  const canViewQueries = checkPermissions(userPermissions, [{ customerApplication: "Google", permissionName: "Queries", action: "read", }]);
  const canViewOliveBatches = checkPermissions(userPermissions, [{ customerApplication: "OLiVE", permissionName: "Batches", action: "read" }]);
  const canViewOliveScripts = checkPermissions(userPermissions, [{ customerApplication: "OLiVE", permissionName: "Scripts", action: "read" }]);
  const canViewProjects = checkPermissions(userPermissions, [{ customerApplication: "Google", permissionName: "Projects", action: "read" }]);
  const hasExtensionRequestsPermission = checkPermissions(userPermissions, [{ customerApplication: "Google", permissionName: "Extension Requests", action: "read" }]);
  const canViewExtensionRequests = canViewProjects && hasExtensionRequestsPermission;

  const getUserDashboards = (selectedApp:any) : Record<string, MenuProps["items"]> => {

    // Check an app has been passed if not return an empty array
    if (!selectedApp) {
      return {};
    }

    // Filter the dashboards to just the dashboards for the selected app
    const appDashboards = userDetails.assignedDashboards
      ?.filter((dashboard:any) => dashboard.customerApplication === selectedApp)
      .map((dashboard:any) => ({
        ...dashboard,
        route: normalizeDashboardRoute(dashboard.route),
      }))
      .filter((dashboard:any) => dashboard.route)

    // Return if no dashboards
    if (appDashboards.length === 0) {
      return {};
    }

    // Sort the dashboards alphabetically
    appDashboards.sort((a:any, b:any) => a.name.localeCompare(b.name));

    // Choose whether to display a drop down menu or just a tab depending 
    // on if the user has more than one dashboard
    if (appDashboards.length === 1) {
      // Return with a single nav tab
      const dashboardMenu = {
        label: <Link to={appDashboards[0].route}>Home</Link>,
        key : "home"
      }

      return {
        dashboard: [dashboardMenu]
      }
    } else {
      // Return with the dashboard home menu
      const dashboardMenu = {
        label : (<>
          Home {!mobile && <DownOutlined />}
        </>),
        key : "home",
        popupClassName: "search-dropdown-menu",
          children: appDashboards.map((dashboard:any) => {
            return {
              label: <Link to={dashboard.route}>{dashboard.name}</Link>,
              key: dashboard.id
            }
          })
      }

      return {
        dashboard: [dashboardMenu]
      }
    }
  }

  // Map selectedApp ID to full app object
  const selectedAppObj = useMemo(() => {
    if (!selectedApp || !userDetails.customerApplicationDetails || userDetails?.customerApplicationDetails?.length === 0) return null;
    return userDetails.customerApplicationDetails.find((a:any) => a._id === selectedApp) || null;
  }, [selectedApp, userDetails.customerApplicationDetails]);

  const googleSearchRoute = useMemo(() => {
    return getFirstAccessibleGoogleRoute(userPermissions, userDetails);
  }, [userPermissions, userDetails]);

  const googleMenu = [
    ...[!canViewProjects ? [] : {
      label: (
        <Link
          to={googleSearchRoute}
          onClick={() => adminMenuData.handleMainMenuSelection("projects")}
        >
          Search {!mobile && <DownOutlined />}
        </Link>
      ),
      key: "search",
      popupClassName: "search-dropdown-menu",
      children: [
        ...[canViewProjects && { label: <Link to={ROUTES.GOOGLE_PROJECTS}>Projects</Link>, key: "projects" }],
        ...[canViewProjects && { label: <Link to={ROUTES.GOOGLE_PLPS}>Plps</Link>, key: "plps" }],
        ...[canViewProjects && { label: <Link to={ROUTES.GOOGLE_DOCUMENTS}>Documents</Link>, key: "documents" }],
        ...[canViewProjects && { label: <Link to={ROUTES.GOOGLE_LINEITEM_VAR}>LineItem VAR</Link>, key: "var" }],
      ],
    }],
    ...(canViewManualProjects ? [{
      label: <Link to={ROUTES.GOOGLE_MANUAL_PROJECTS}>Manual Projects</Link>,
      key: "manual-projects",
    }] : []),
    ...(canViewProjects ? [{ label: <Link to={ROUTES.GOOGLE_INVOICING}>Invoicing</Link>, key: "invoicing" }] : []),
    (userDetails.vendorManager || isAdmin) && {
      label: <Link to={ROUTES.GOOGLE_LINGUIST_MANAGEMENT}>Linguist Management</Link>,
      key: "linguist-management",
    },
    { label: <Link to={ROUTES.GOOGLE_REPORTS}>Reports</Link>, key: "reports" },
    ...(canViewExtensionRequests ? [{
      label: <Link to={ROUTES.GOOGLE_EXTENSION_REQUESTS}>Extension Requests</Link>,
      key: "extension-requests",
    }] : []),
    (flags?.adjustmentsTab || isAdmin) && canViewAdjustments && {
      label: <Link to={ROUTES.GOOGLE_ADJUSTMENTS}>Adjustments</Link>,
      key: "adjustments",
    },
    canViewQueries && { label: <Link to={ROUTES.GOOGLE_QUERIES}>Queries</Link>, key: "queries" }
  ]

  const oliveMenu = [
    canViewOliveBatches && { label: <Link to={ROUTES.OLIVE_BATCHES}>Batches</Link>, key: "batches" },
    canViewOliveScripts && { label: <Link to={ROUTES.OLIVE_SCRIPTS}>Scripts</Link>, key: "scripts" },
  ].filter(Boolean) as MenuProps["items"];

  const menuConfig: Record<string, MenuProps["items"]> = {
    google: googleMenu,
    olive: oliveMenu,
    default: [
      { label: <Link to={ROUTES.MAIN}>Home</Link>, key: "home" },
    ],
  };

 // Determine menu items based on selected app
 const items = useMemo(() => {
  if (!selectedAppObj?.name) return menuConfig.default;
  const appMenuItems = menuConfig[selectedAppObj.name.toLowerCase()] || menuConfig.default;
  const dashboardMenuItem = getUserDashboards(selectedAppObj._id);
  if (appMenuItems) {
    if (dashboardMenuItem.dashboard) {
      return [...dashboardMenuItem.dashboard, ...appMenuItems];
    } else {
      return appMenuItems;
    }
  } else if (dashboardMenuItem.dashboard) {
    return dashboardMenuItem.dashboard;
  }
}, [selectedAppObj]);

// Mapping paths to menu keys for active highlighting
  const pathToKeyMap: Record<string, string> = {
    "/google/projects": "projects",
    "/google/projects/:id": "projects",
    "/google/manual-projects": "manual-projects",
    "/google/manual-projects/:id": "manual-projects",
    "/google/plps": "plps",
    "/google/plps/:id": "plps",
    "/google/documents": "documents",
    "/google/documents/:id": "documents",
    "/google/var": "var",
    "/google/invoicing": "invoicing",
    "/google/invoicing/:id": "invoicing",
    "/google/linguist-management": "linguist-management",
    "/google/reports": "reports",
    "/google/reports/my-reports": "reports",
    "/google/extension-requests": "extension-requests",
    "/google/adjustments": "adjustments",
    "/google/adjustments/:id": "adjustments",
    "/google/queries": "queries",
    "/google/queries/:id": "queries",

    // Olive routes
    "/olive/batches": "batches",
    "/olive/batches/:id": "batches",
    "/olive/scripts": "scripts",
    "/olive/scripts/:id": "scripts",
    "/olive/dashboard/my-scripts": "my-scripts",
    "/olive/dashboard/pm": "dashboard",

  };

const getActiveKey = () => {
  const currentPath = location.pathname;
  for (const [path, key] of Object.entries(pathToKeyMap)) {
    const dynamicPathRegex = new RegExp(path.replace(/:[^/]+/g, "[^/]+") + "$");
    if (dynamicPathRegex.test(currentPath)) return key;
  }
  return "";
};

const activeKey = getActiveKey();

// Reset menu selection if current path doesn't match menu items
useEffect(() => {
  const allItems = items
    ?.flatMap((item) =>
      item && "children" in item && Array.isArray(item.children)
        ? [...item.children, item]
        : item
    )
    .filter(Boolean);

  const path =
    location?.pathname === ROUTES.GOOGLE_MAIN || location.pathname.includes(`${ROUTES.GOOGLE_PROJECTS}/`)
      ? "search"
      : location?.pathname.replaceAll(ROUTES.GOOGLE_MAIN, "");

  const isUsed = allItems?.some((i) => path.includes(i?.key as string));

  if (!isUsed) {
    adminMenuData.handleMainMenuSelection(null);
  }
}, [location, items, adminMenuData]);

// Show nothing until apps are loaded
if (!userDetails.customerApplicationDetails) return null;

  return (
    <Menu
      onClick={(e) => adminMenuData.handleMainMenuSelection(e.key as string)}
      selectedKeys={[activeKey]}
      mode={mobile ? "inline" : "horizontal"}
      items={items}
    />
  );
};

export default LeftMenu;
